import React from "react";
import {
  Document,
  Page,
  Text,
  View,
  StyleSheet,
  PDFDownloadLink,
  pdf,
  Font,
  Link,
} from "@react-pdf/renderer";
import { HtmlToReactPdf } from "./HtmlToReactPdf";

// Create styles for PDF
const styles = StyleSheet.create({
  page: {
    padding: 0,
    fontSize: 11,
    fontFamily: "Helvetica",
    lineHeight: 1.4,
    backgroundColor: "#ffffff",
  },
  pageContent: {
    width: "100%",
    height: "100%",
    position: "relative",
  },
  // --- Template Styles ---
  classic: { fontFamily: "Times-Roman", padding: "50 50 40 50" },
  classicName: {
    fontSize: 24,
    fontWeight: "bold",
    textAlign: "center",
    marginBottom: 8,
  },
  classicSectionTitle: {
    fontSize: 12,
    fontWeight: "bold",
    marginTop: 15,
    marginBottom: 5,
    borderBottomWidth: 1,
    textTransform: "uppercase",
  },
  modern: { fontFamily: "Helvetica", padding: "50 50 40 50" },
  modernName: {
    fontSize: 28,
    fontWeight: 600,
    color: "#065f46",
    textAlign: "center",
    marginBottom: 8,
  },
  modernSectionTitle: {
    fontSize: 11,
    fontWeight: 600,
    color: "#065f46",
    marginTop: 18,
    marginBottom: 6,
    borderBottomWidth: 1,
    borderBottomColor: "#e5e7eb",
    textTransform: "uppercase",
  },
  executive: { fontFamily: "Helvetica", padding: "40 40 30 40" },
  executiveName: {
    fontSize: 32,
    fontWeight: 700,
    color: "#2d2d2d",
    textAlign: "center",
    marginBottom: 5,
    textTransform: "uppercase",
  },
  executiveSectionTitle: {
    fontSize: 12,
    fontWeight: 600,
    color: "#333333",
    marginTop: 20,
    marginBottom: 8,
    borderBottomWidth: 2,
    textTransform: "uppercase",
  },
  // --- Common Styles ---
  paragraph: { marginBottom: 4, fontSize: 11, lineHeight: 1.4 },
});

// Resume PDF Document Component with Multi-Page Support
const ResumePdfDocument = ({
  content,
  templateId = "modern",
  structuredData = null,
  pagesData = null,
}) => {
  // Add console logging for debugging
  console.log("=== ResumePdfDocument Debug Info ===");
  console.log("templateId:", templateId);
  console.log("structuredData:", structuredData);
  console.log("pagesData:", pagesData);
  console.log(
    "content (single page):",
    content ? content.substring(0, 200) + "..." : "null"
  );

  const getPageStyle = (templateId) => {
    switch (templateId) {
      case "classic":
        return { ...styles.page, ...styles.classic };
      case "executive":
        return { ...styles.page, ...styles.executive };
      default:
        return { ...styles.page, ...styles.modern };
    }
  };

  const getTemplateStyles = (templateId) => {
    switch (templateId) {
      case "classic":
        return {
          heading1: styles.classicName,
          heading3: styles.classicSectionTitle,
        };
      case "executive":
        return {
          heading1: styles.executiveName,
          heading3: styles.executiveSectionTitle,
        };
      default:
        return {
          heading1: styles.modernName,
          heading3: styles.modernSectionTitle,
        };
    }
  };

  // Handle multi-page content from editor
  if (pagesData && pagesData.length > 0) {
    console.log("=== Multi-page PDF Generation ===");
    console.log("Number of pages:", pagesData.length);

    // Log each page's content
    pagesData.forEach((page, index) => {
      console.log(
        `Page ${index + 1} content preview:`,
        page.content ? page.content.substring(0, 150) + "..." : "empty"
      );
      console.log(`Page ${index + 1} height:`, page.height);
    });

    // --- THIS IS THE KEY FIX ---
    // Create ONE parser instance that will be used for ALL pages.
    // This allows it to remember that it has already seen the H1 when it gets to page 2.
    const htmlParser = new HtmlToReactPdf(getTemplateStyles(templateId));
    console.log("Created single HtmlToReactPdf instance for all pages");
    // --- END OF KEY FIX ---

    return (
      <Document>
        {pagesData.map((pageData, pageIndex) => {
          const pageContent = pageData.content || "";
          console.log(
            `Parsing page ${pageIndex + 1} with shared parser instance`
          );
          console.log(`Parser state before page ${pageIndex + 1}:`, {
            h1Rendered: htmlParser.parsingState.h1Rendered,
            contactInfoRendered: htmlParser.parsingState.contactInfoRendered,
          });

          // Use the SAME parser instance for each page.
          const parsedContent = htmlParser.parse(pageContent);

          console.log(`Parser state after page ${pageIndex + 1}:`, {
            h1Rendered: htmlParser.parsingState.h1Rendered,
            contactInfoRendered: htmlParser.parsingState.contactInfoRendered,
          });

          return (
            <Page
              key={`page-${pageIndex}`}
              size="LETTER"
              style={getPageStyle(templateId)}
              wrap={false}
            >
              <View style={styles.pageContent}>{parsedContent}</View>
            </Page>
          );
        })}
      </Document>
    );
  }

  // Fallback for single-page content
  console.log("=== Single-page PDF Generation ===");
  const htmlParser = new HtmlToReactPdf(getTemplateStyles(templateId));
  const parsedContent = htmlParser.parse(content);

  return (
    <Document>
      <Page size="LETTER" style={getPageStyle(templateId)} wrap={false}>
        <View style={styles.pageContent}>{parsedContent}</View>
      </Page>
    </Document>
  );
};

// Function to generate PDF blob with multi-page support
export const generatePdfBlob = async (
  content,
  templateId,
  structuredData = null,
  pagesData = null
) => {
  console.log("=== generatePdfBlob called ===");
  console.log("Arguments:", {
    contentLength: content ? content.length : 0,
    templateId,
    hasStructuredData: !!structuredData,
    pagesDataLength: pagesData ? pagesData.length : 0,
  });

  try {
    const doc = (
      <ResumePdfDocument
        content={content}
        templateId={templateId}
        structuredData={structuredData}
        pagesData={pagesData}
      />
    );
    const blob = await pdf(doc).toBlob();
    console.log("PDF blob generated successfully, size:", blob.size);
    return blob;
  } catch (error) {
    console.error("Error generating PDF blob:", error);
    throw error;
  }
};

export default ResumePdfDocument;
